import React, { useCallback, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ClipboardCheck, RefreshCw, User, ChevronRight, ChevronDown, Calendar } from 'lucide-react';
import { EHS_DAY_TITLES, EHS_QUESTIONS } from '../../constants/ehsQuestions';

const API = process.env.REACT_APP_API_BASE_URL;

// ─── NOVAA dark tokens (match CIDashboard) ───────────────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border:  'var(--nd-w06)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx40)',
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .ehs-wrap { font-family: 'DM Sans', sans-serif; color: var(--nd-text); }
  .ehs-input {
    background: var(--nd-w05); border: 1px solid var(--nd-w12);
    border-radius: 9px; color: var(--nd-text); font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 600; padding: 7px 12px; outline: none;
  }
  .ehs-input:focus { border-color: rgba(96,165,250,0.5); }
  .ehs-btn {
    background: var(--nd-w05); border: 1px solid var(--nd-w12);
    border-radius: 9px; color: var(--nd-tx70); font-weight: 700;
    font-size: 12px; padding: 8px 14px; cursor: pointer; display: inline-flex;
    align-items: center; gap: 7px; transition: all 0.15s;
  }
  .ehs-btn:hover { background: var(--nd-w10); color: var(--nd-text-strong); }
  
  /* Tables */
  .ehs-table { width: 100%; border-collapse: collapse; }
  .ehs-table th {
    text-align: left; font-size: 9px; font-weight: 700; letter-spacing: 0.15em;
    color: var(--nd-tx35); text-transform: uppercase; padding: 10px 14px;
    border-bottom: 1px solid var(--nd-w08);
  }
  .ehs-table td {
    font-size: 13px; color: var(--nd-text); padding: 12px 14px;
    border-bottom: 1px solid var(--nd-w04);
  }
  
  .ehs-row { cursor: pointer; transition: background 0.15s; }
  .ehs-row:hover { background: var(--nd-w03); }
  
  .ehs-sub-row { cursor: pointer; background: var(--nd-w02); transition: background 0.15s; }
  .ehs-sub-row:hover { background: var(--nd-w04); }
  
  /* Badges */
  .ehs-count {
    font-family: 'DM Mono', monospace; font-weight: 700; font-size: 12px;
    padding: 3px 10px; border-radius: 6px; display: inline-block; min-width: 40px; text-align: center;
  }
  .ehs-correct { color: #22D3A5; background: rgba(34,211,165,0.12); border: 1px solid rgba(34,211,165,0.25); }
  .ehs-incorrect { color: #F87171; background: rgba(248,113,113,0.12); border: 1px solid rgba(248,113,113,0.25); }
  .ehs-neutral { color: var(--nd-tx40); background: var(--nd-w04); border: 1px solid var(--nd-w08); }
  
  /* Inner Q&A */
  .ehs-qrow {
    display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
    padding: 12px 0; border-bottom: 1px dashed var(--nd-w06);
  }
  .ehs-qrow:last-child { border-bottom: none; }
`;

const todayStr = () => new Date().toISOString().split('T')[0];
const monthStartStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const dayTitle = (idx) =>
  (idx >= 0 && idx < EHS_DAY_TITLES.length) ? EHS_DAY_TITLES[idx] : `Day ${Number(idx) + 1}`;


// ─── CORRECT ANSWERS CONFIGURATION ───────────────────────────────
const EXPECTED_ANSWERS = {
  q1: 'no',   q2: 'yes',  q3: 'yes',  q4: 'yes',  q5: 'yes',
  q6: 'yes',  q7: 'no',   q8: 'yes',  q9: 'no',   q10: 'no',
  q11: 'yes', q12: 'yes', q13: 'yes', q14: 'no',   q15: 'yes',
  q16: 'no',  q17: 'yes', q18: 'yes', q19: 'yes', q20: 'no',
  q21: 'no',  q22: 'yes', q23: 'yes', q24: 'yes', q25: 'yes',
  q26: 'no',  q27: 'yes', q28: 'no',  q29: 'yes', q30: 'yes',
  q31: 'yes', q32: 'yes', q33: 'yes', q34: 'yes', q35: 'yes',
  q36: 'yes', q37: 'yes', q38: 'yes', q39: 'yes', q40: 'yes',
  q41: 'yes', q42: 'no',  q43: 'yes', q44: 'yes', q45: 'no',
  q46: 'no',  q47: 'yes', q48: 'yes', q49: 'yes', q50: 'yes',
  q51: 'no',  q52: 'yes', q53: 'yes', q54: 'no',  q55: 'yes',
  q56: 'yes', q57: 'yes', q58: 'yes', q59: 'yes', q60: 'yes',
  q61: 'yes', q62: 'no',  q63: 'yes', q64: 'yes', q65: 'no',
  q66: 'yes', q67: 'yes', q68: 'yes', q69: 'no',  q70: 'yes',
  q71: 'yes', q72: 'no',  q73: 'no',  q74: 'yes', q75: 'yes',
  q76: 'yes', q77: 'no',  q78: 'yes', q79: 'yes', q80: 'yes',
  q81: 'yes', q82: 'no',  q83: 'yes', q84: 'yes', q85: 'yes',
  q86: 'yes', q87: 'yes', q88: 'yes', q89: 'yes', q90: 'yes',
};

const isCorrectAnswer = (qid, userAns) => {
  const expected = EXPECTED_ANSWERS[qid] || 'yes';
  return String(userAns).toLowerCase() === expected.toLowerCase();
};

const evaluateAnswers = (answers) => {
  let correct = 0, incorrect = 0;
  if (answers && typeof answers === 'object') {
    for (const [qid, v] of Object.entries(answers)) {
      if (isCorrectAnswer(qid, v)) correct += 1;
      else incorrect += 1;
    }
  }
  return { correct, incorrect, total: correct + incorrect };
};

const sortedAnswers = (answers) =>
  Object.entries(answers || {}).sort(
    (a, b) => (parseInt(a[0].slice(1), 10) || 0) - (parseInt(b[0].slice(1), 10) || 0)
  );

const EHSReports = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [from, setFrom] = useState(monthStartStr());
  const [to, setTo] = useState(todayStr());
  
  const [expandedUsers, setExpandedUsers] = useState(() => new Set());
  const [expandedSubs, setExpandedSubs] = useState(() => new Set());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setExpandedUsers(new Set());
      setExpandedSubs(new Set());
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      const res = await axios.get(`${API}/ehs/submissions?${params.toString()}`);
      setRows(res.data?.data || []);
    } catch (err) {
      console.error('EHS fetch error:', err);
      setError('Failed to load EHS submissions.');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const groupedData = useMemo(() => {
    const map = {};
    let grandTotalIncorrect = 0;

    rows.forEach(r => {
      const personId = r.user_id || r.full_name || 'unknown';
      const name = r.full_name || `User #${r.user_id}`;
      
      if (!map[personId]) {
        map[personId] = {
          id: personId,
          name: name,
          role: r.role || 'N/A',
          totalCorrect: 0,
          totalIncorrect: 0,
          totalQuestions: 0,
          submissions: []
        };
      }

      const evalResult = evaluateAnswers(r.answers);
      
      map[personId].totalCorrect += evalResult.correct;
      map[personId].totalIncorrect += evalResult.incorrect;
      map[personId].totalQuestions += evalResult.total;
      grandTotalIncorrect += evalResult.incorrect;

      map[personId].submissions.push({
        ...r,
        eval: evalResult
      });
    });

    const groupedArray = Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
    
    groupedArray.forEach(user => {
      user.submissions.sort((a, b) => new Date(b.submission_date) - new Date(a.submission_date));
    });

    return { groupedArray, grandTotalIncorrect };
  }, [rows]);

  const { groupedArray, grandTotalIncorrect } = groupedData;

  const toggleUser = (id) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSub = (id) => {
    setExpandedSubs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="ehs-wrap" style={{ background: T.bg, padding: '14px 20px', minHeight: '100vh' }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: '20px 24px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: 'rgba(34,211,165,0.12)', border: '1px solid rgba(34,211,165,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ClipboardCheck size={22} color="#22D3A5" />
            </div>
            <div>
              <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 4 }}>NOVAA · C&I SAFETY</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                Daily EHS Checklists
              </div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>Track correct/incorrect EHS checklist responses per employee</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 9, color: T.muted, letterSpacing: '0.12em' }}>
              FROM
              <input type="date" className="ehs-input" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 9, color: T.muted, letterSpacing: '0.12em' }}>
              TO
              <input type="date" className="ehs-input" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button className="ehs-btn" onClick={fetchData} title="Refresh">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', color: 'var(--nd-tx30)' }}>PERSON-WISE CHECKLISTS</div>
          <div style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Mono', monospace" }}>
            {loading ? '…' : `${groupedArray.length} employee${groupedArray.length === 1 ? '' : 's'}${grandTotalIncorrect > 0 ? ` · ${grandTotalIncorrect} Incorrect Answers` : ''}`}
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: T.muted, fontSize: 13 }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#F87171', fontSize: 13 }}>{error}</div>
        ) : groupedArray.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: T.muted, fontSize: 13 }}>
            No EHS submissions in this date range.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="ehs-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>Employee Name</th>
                  <th>Role</th>
                  <th style={{ textAlign: 'center' }}>Submissions</th>
                  <th style={{ textAlign: 'center' }}>Correct Answers</th>
                  <th style={{ textAlign: 'center' }}>Incorrect Answers</th>
                </tr>
              </thead>
              <tbody>
                {groupedArray.map((user) => {
                  const isUserOpen = expandedUsers.has(user.id);
                  return (
                    <React.Fragment key={user.id}>
                      {/* USER ROW */}
                      <tr className="ehs-row" onClick={() => toggleUser(user.id)}>
                        <td style={{ color: 'var(--nd-tx45)', textAlign: 'center' }}>
                          {isUserOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <User size={14} color="var(--nd-tx35)" />
                            <span style={{ fontWeight: 700 }}>{user.name}</span>
                          </div>
                        </td>
                        <td style={{ color: T.muted, fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{user.role}</td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{user.submissions.length}</td>
                        
                        {/* Overall Correct */}
                        <td style={{ textAlign: 'center' }}>
                          <span className={`ehs-count ${user.totalCorrect > 0 ? 'ehs-correct' : 'ehs-neutral'}`}>
                            {user.totalCorrect} <span style={{ fontSize: 9, opacity: 0.7 }}>/ {user.totalQuestions}</span>
                          </span>
                        </td>
                        
                        {/* Overall Incorrect */}
                        <td style={{ textAlign: 'center' }}>
                          <span className={`ehs-count ${user.totalIncorrect > 0 ? 'ehs-incorrect' : 'ehs-neutral'}`}>
                            {user.totalIncorrect} <span style={{ fontSize: 9, opacity: 0.7 }}>/ {user.totalQuestions}</span>
                          </span>
                        </td>
                      </tr>

                      {/* USER SUBMISSIONS EXPANDED */}
                      {isUserOpen && (
                        <tr>
                          <td colSpan={6} style={{ padding: 0, background: 'var(--nd-w02)', borderBottom: `1px solid ${T.border}` }}>
                            <div style={{ padding: '8px 14px 8px 46px' }}>
                              <table className="ehs-table" style={{ background: 'var(--nd-surface)', border: `1px solid var(--nd-w06)`, borderRadius: 8, overflow: 'hidden' }}>
                                <thead>
                                  <tr>
                                    <th style={{ width: 36, background: 'var(--nd-w03)' }}></th>
                                    <th style={{ background: 'var(--nd-w03)' }}>Date</th>
                                    <th style={{ background: 'var(--nd-w03)' }}>Checklist Day</th>
                                    <th style={{ textAlign: 'center', background: 'var(--nd-w03)' }}>Correct</th>
                                    <th style={{ textAlign: 'center', background: 'var(--nd-w03)' }}>Incorrect</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {user.submissions.map((sub) => {
                                    const isSubOpen = expandedSubs.has(sub.id);
                                    return (
                                      <React.Fragment key={sub.id}>
                                        {/* DATE/SUBMISSION ROW */}
                                        <tr className="ehs-sub-row" onClick={() => toggleSub(sub.id)}>
                                          <td style={{ color: 'var(--nd-tx45)', textAlign: 'center' }}>
                                            {isSubOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                          </td>
                                          <td style={{ fontFamily: "'DM Mono', monospace", color: 'var(--nd-tx70)', fontWeight: 600 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                              <Calendar size={12} color="var(--nd-tx35)" />
                                              {sub.submission_date || '—'}
                                            </div>
                                          </td>
                                          <td style={{ fontWeight: 600 }}>{dayTitle(sub.day_index)}</td>
                                          
                                          {/* Daily Correct */}
                                          <td style={{ textAlign: 'center' }}>
                                            <span className={`ehs-count ${sub.eval.correct > 0 ? 'ehs-correct' : 'ehs-neutral'}`} style={{ padding: '2px 8px', fontSize: 11 }}>
                                              {sub.eval.correct} <span style={{ fontSize: 8, opacity: 0.7 }}>/ {sub.eval.total}</span>
                                            </span>
                                          </td>
                                          
                                          {/* Daily Incorrect */}
                                          <td style={{ textAlign: 'center' }}>
                                            <span className={`ehs-count ${sub.eval.incorrect > 0 ? 'ehs-incorrect' : 'ehs-neutral'}`} style={{ padding: '2px 8px', fontSize: 11 }}>
                                              {sub.eval.incorrect} <span style={{ fontSize: 8, opacity: 0.7 }}>/ {sub.eval.total}</span>
                                            </span>
                                          </td>
                                        </tr>

                                        {/* Q&A EXPANDED */}
                                        {isSubOpen && (
                                          <tr>
                                            <td colSpan={5} style={{ padding: 0, background: 'var(--nd-bg)' }}>
                                              <div style={{ padding: '12px 24px 16px 48px' }}>
                                                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--nd-tx30)', marginBottom: 10 }}>
                                                  CHECKLIST RESPONSES
                                                </div>
                                                <div style={{ background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)', borderRadius: 8, padding: '0 16px' }}>
                                                  {sortedAnswers(sub.answers).map(([qid, ans]) => {
                                                    const correct = isCorrectAnswer(qid, ans);
                                                    const expected = EXPECTED_ANSWERS[qid] || 'yes';
                                                    
                                                    return (
                                                      <div key={qid} className="ehs-qrow">
                                                        <div style={{ fontSize: 13, color: '#CBD5E1', lineHeight: 1.5, flex: 1, paddingRight: 20 }}>
                                                          <span style={{ color: 'var(--nd-tx40)', marginRight: 6 }}>{qid.toUpperCase()}.</span>
                                                          {EHS_QUESTIONS[qid] || qid}
                                                        </div>
                                                        
                                                        {/* Result Badge */}
                                                        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                                                          <span className={`ehs-count ${correct ? 'ehs-correct' : 'ehs-incorrect'}`} style={{ fontSize: 11, minWidth: 70 }}>
                                                            {correct ? 'Sahi ✓' : 'Ghalat ✕'}
                                                          </span>
                                                          
                                                          {/* What user answered vs What was expected */}
                                                          <div style={{ fontSize: 10, color: 'var(--nd-tx40)', fontFamily: "'DM Mono', monospace", background: 'var(--nd-w03)', padding: '4px 8px', borderRadius: 6, border: '1px solid var(--nd-w05)' }}>
                                                            <div style={{ marginBottom: 2 }}>
                                                              User: <span style={{ color: correct ? '#22D3A5' : '#F87171', fontWeight: 700 }}>{String(ans).toUpperCase()}</span>
                                                            </div>
                                                            <div>
                                                              Sahi: <span style={{ color: 'var(--nd-text)', fontWeight: 600 }}>{expected.toUpperCase()}</span>
                                                            </div>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EHSReports;